/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BankAccount, Firm } from '../firm.model';
import { FirmService } from '../firm.service';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { ToastMessageOptions } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BankAccountDialogComponent } from './bank-account-dialog/bank-account-dialog.component';
import { TranslateService } from '@ngx-translate/core';

//TODO rename to iriro fwl-firm-shared
@Component({
  selector: 'fwl-firm-shared, iriro-firm-shared ',
  templateUrl: './firm-shared.component.html',
  styleUrls: ['./firm-shared.component.scss'],
})
export class FirmSharedComponent implements OnInit {
  @Input() form!: UntypedFormGroup;
  @Input() new!: boolean;

  @Output() foundFirm = new EventEmitter<boolean>();

  found!: boolean;
  firms!: Firm[];
  msgs: ToastMessageOptions[] = [];
  bankAccounts: BankAccount[] = [];
  editableBankAccount!: BankAccount | null;

  createBankAccountHeader!: string;

  constructor(
    private readonly firmService: FirmService,
    private readonly translateService: TranslateService,
    private readonly dialogService: DialogService,
    private dynamicDialogRef: DynamicDialogRef
  ) {}

  get bankAccountsControl(): FormControl {
    return this.form.get('bankAccounts') as FormControl;
  }

  ngOnInit() {
    if (this.new) this.form.reset();
    this.translateService.stream(['fwl.createBankAccount']).subscribe({
      next: (res) => {
        this.createBankAccountHeader = res['fwl.createBankAccount'];
      },
    });
    this.editableBankAccount = null;
    this.found = false;
  }

  deleteBankAccount(bankAccount: BankAccount) {
    const index: number = this.bankAccounts.indexOf(bankAccount);
    if (index !== -1) {
      this.bankAccounts.splice(index, 1);
    }
  }

  editBankAccount(bankAccount: BankAccount) {
    this.editableBankAccount = bankAccount;
  }

  saveBankAccount() {
    this.editableBankAccount = null;
  }

  newAccount() {
    this.dynamicDialogRef = this.dialogService.open(
      BankAccountDialogComponent,
      {
        header: this.createBankAccountHeader,
        width: '50%',
        contentStyle: { 'max-height': '500px', overflow: 'auto' },
        baseZIndex: 10000,
      }
    );
    this.dynamicDialogRef.onClose.subscribe({
      next: (bankAccount) => {
        if (bankAccount) {
          this.bankAccounts.push(bankAccount);
          this.bankAccountsControl.patchValue(this.bankAccounts);
        }
      },
    });
  }

  reset() {
    this.found = false;
    this.form.reset();
    this.msgs = [];
    this.foundFirm.emit(false);
  }

  showWarn() {
    this.msgs = [];
    this.msgs.push({
      severity: 'warn',
      summary: 'Atentie!',
      detail: 'Firma exista deja, nu se mai poate adauga',
    });
  }

  filterFirm(query: string, field: any) {
    const lazyEvent = {
      filters: {} as Record<string, any>,
    };
    lazyEvent.filters[field] = {
      value: query,
      matchMode: 'contains',
    };
    this.firmService.getFirms(lazyEvent).subscribe((data: any) => {
      this.firms =
        data.page.totalElements > 0 ? data._embedded.entityModels : [];
    });
  }

  onAutoCompleteSelect(firm: Firm) {
    this.form.patchValue(firm);
    this.found = true;
    this.foundFirm.emit(this.found);
    this.showWarn();
  }

  getFirmDisplayLabel = (firm: Firm | null): string => {
    return firm?.name ?? '';
  };

  compareFirmByName = (a: Firm | null, b: Firm | null): boolean => {
    return a?.name === b?.name;
  };
}
